import { isOrgPrimaryReportingEdge } from '../../../domains/orgchart/connectors/routingPolicies';
import type { StructuredQuery, QueryHandler } from './StructuredQA';

interface NodeData {
  id: string;
  type: string;
  data?: Record<string, unknown>;
}

interface EdgeData {
  id: string;
  type: string;
  from: string;
  to: string;
  attrs?: Record<string, unknown>;
}

export interface QueryResult {
  success: boolean;
  nodeIds: string[];
  message: string;
  count?: number;
  data?: unknown;
}

interface QueryContext {
  nodes: NodeData[];
  edges: EdgeData[];
}

type QueryHandlerFn = (params: string[], context: QueryContext) => QueryResult;

export class QueryExecutor {
  private handlers: Map<QueryHandler, QueryHandlerFn>;

  constructor() {
    this.handlers = new Map([
      ['findDirectReports', this.findDirectReports.bind(this)],
      ['findManager', this.findManager.bind(this)],
      ['filterToTeam', this.filterToTeam.bind(this)],
      ['filterToDepartment', this.filterToDepartment.bind(this)],
      ['countInDepartment', this.countInDepartment.bind(this)],
      ['findByName', this.findByName.bind(this)],
      ['findByTitle', this.findByTitle.bind(this)],
      ['showReportingChain', this.showReportingChain.bind(this)],
      ['findLongestTenure', this.findLongestTenure.bind(this)],
      ['findBySkill', this.findBySkill.bind(this)],
    ]);
  }

  execute(query: StructuredQuery, context: QueryContext): QueryResult {
    const handler = this.handlers.get(query.type);

    if (!handler) {
      return {
        success: false,
        nodeIds: [],
        message: `Unknown query type: ${query.type}`,
      };
    }

    try {
      return handler(query.params, context);
    } catch (error) {
      return {
        success: false,
        nodeIds: [],
        message: `Error executing query: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  registerHandler(type: QueryHandler, handler: QueryHandlerFn): void {
    this.handlers.set(type, handler);
  }

  private findDirectReports(params: string[], context: QueryContext): QueryResult {
    const [targetName] = params;
    if (!targetName) {
      return { success: false, nodeIds: [], message: 'No name specified' };
    }

    const targetNode = this.findNodeByName(targetName, context.nodes);
    if (!targetNode) {
      return { success: false, nodeIds: [], message: `Could not find "${targetName}"` };
    }

    const nodeIds = new Set(context.nodes.map((node) => node.id));
    const reportIds = [...new Set(context.edges
      .filter((edge) => edge.to === targetNode.id && edge.from !== edge.to && nodeIds.has(edge.from) && this.isReportingEdge(edge))
      .map((edge) => edge.from))];

    return {
      success: true,
      nodeIds: reportIds,
      message: `Found ${reportIds.length} direct report${reportIds.length !== 1 ? 's' : ''} for ${targetName}`,
      count: reportIds.length,
    };
  }

  private findManager(params: string[], context: QueryContext): QueryResult {
    const [targetName] = params;
    if (!targetName) {
      return { success: false, nodeIds: [], message: 'No name specified' };
    }

    const targetNode = this.findNodeByName(targetName, context.nodes);
    if (!targetNode) {
      return { success: false, nodeIds: [], message: `Could not find "${targetName}"` };
    }

    const managerEdge = context.edges.find(
      (edge) => edge.from === targetNode.id && this.isReportingEdge(edge)
    );

    if (!managerEdge) {
      return { success: true, nodeIds: [], message: `${targetName} has no manager` };
    }

    const manager = context.nodes.find((n) => n.id === managerEdge.to);
    const managerName = manager?.data?.['name'] ?? managerEdge.to;

    return {
      success: true,
      nodeIds: [managerEdge.to],
      message: `${targetName} reports to ${managerName}`,
    };
  }

  private filterToTeam(params: string[], context: QueryContext): QueryResult {
    const [teamName] = params;
    if (!teamName) {
      return { success: false, nodeIds: [], message: 'No team specified' };
    }

    const matchingNodes = context.nodes.filter((node) => {
      const team = node.data?.['team'] ?? node.data?.['department'];
      return typeof team === 'string' && team.toLowerCase().includes(teamName.toLowerCase());
    });

    return {
      success: true,
      nodeIds: matchingNodes.map((n) => n.id),
      message: `Found ${matchingNodes.length} people in ${teamName} team`,
      count: matchingNodes.length,
    };
  }

  private filterToDepartment(params: string[], context: QueryContext): QueryResult {
    const [deptName] = params;
    if (!deptName) {
      return { success: false, nodeIds: [], message: 'No department specified' };
    }

    const matchingNodes = context.nodes.filter((node) => {
      const dept = node.data?.['department'] ?? node.data?.['team'];
      return typeof dept === 'string' && dept.toLowerCase().includes(deptName.toLowerCase());
    });

    return {
      success: true,
      nodeIds: matchingNodes.map((n) => n.id),
      message: `Found ${matchingNodes.length} people in ${deptName} department`,
      count: matchingNodes.length,
    };
  }

  private countInDepartment(params: string[], context: QueryContext): QueryResult {
    const result = this.filterToDepartment(params, context);
    return result;
  }

  private findByName(params: string[], context: QueryContext): QueryResult {
    const [name] = params;
    if (!name) {
      return { success: false, nodeIds: [], message: 'No name specified' };
    }

    const matchingNodes = context.nodes.filter((node) => {
      const nodeName = node.data?.['name'];
      return typeof nodeName === 'string' && nodeName.toLowerCase().includes(name.toLowerCase());
    });

    if (matchingNodes.length === 0) {
      return { success: false, nodeIds: [], message: `No one found matching "${name}"` };
    }

    return {
      success: true,
      nodeIds: matchingNodes.map((n) => n.id),
      message: `Found ${matchingNodes.length} match${matchingNodes.length !== 1 ? 'es' : ''} for "${name}"`,
      count: matchingNodes.length,
    };
  }

  private findByTitle(params: string[], context: QueryContext): QueryResult {
    const [title] = params;
    if (!title) {
      return { success: false, nodeIds: [], message: 'No title specified' };
    }

    const matchingNodes = context.nodes.filter((node) => {
      const nodeTitle = node.data?.['title'] ?? node.data?.['jobTitle'] ?? node.data?.['role'];
      return typeof nodeTitle === 'string' && nodeTitle.toLowerCase().includes(title.toLowerCase());
    });

    return {
      success: true,
      nodeIds: matchingNodes.map((n) => n.id),
      message: `Found ${matchingNodes.length} people with "${title}" title`,
      count: matchingNodes.length,
    };
  }

  private showReportingChain(params: string[], context: QueryContext): QueryResult {
    const [targetName] = params;
    if (!targetName) {
      return { success: false, nodeIds: [], message: 'No name specified' };
    }

    const targetNode = this.findNodeByName(targetName, context.nodes);
    if (!targetNode) {
      return { success: false, nodeIds: [], message: `Could not find "${targetName}"` };
    }

    const chain: string[] = [targetNode.id];
    let currentId = targetNode.id;
    const visited = new Set<string>();

    while (!visited.has(currentId)) {
      visited.add(currentId);

      const managerEdge = context.edges.find(
        (edge) => edge.from === currentId && this.isReportingEdge(edge)
      );

      if (!managerEdge) break;

      chain.push(managerEdge.to);
      currentId = managerEdge.to;
    }

    return {
      success: true,
      nodeIds: chain,
      message: `Reporting chain has ${chain.length} level${chain.length !== 1 ? 's' : ''}`,
      count: chain.length,
    };
  }

  private findLongestTenure(_params: string[], context: QueryContext): QueryResult {
    const nodesWithTenure = context.nodes
      .filter((node) => {
        const startDate = node.data?.['startDate'] ?? node.data?.['hireDate'] ?? node.data?.['joinDate'];
        return startDate != null;
      })
      .map((node) => {
        const startDate = node.data?.['startDate'] ?? node.data?.['hireDate'] ?? node.data?.['joinDate'];
        const date = new Date(startDate as string);
        return { node, date, tenure: Date.now() - date.getTime() };
      })
      .sort((a, b) => b.tenure - a.tenure);

    if (nodesWithTenure.length === 0) {
      return { success: false, nodeIds: [], message: 'No tenure data available' };
    }

    const topTenure = nodesWithTenure.slice(0, 5);

    return {
      success: true,
      nodeIds: topTenure.map((t) => t.node.id),
      message: `Found ${topTenure.length} people with longest tenure`,
      count: topTenure.length,
    };
  }

  private findBySkill(params: string[], context: QueryContext): QueryResult {
    const [skill] = params;
    if (!skill) {
      return { success: false, nodeIds: [], message: 'No skill specified' };
    }

    const matchingNodes = context.nodes.filter((node) => {
      const skills = node.data?.['skills'];

      if (Array.isArray(skills)) {
        return skills.some((s) =>
          typeof s === 'string' && s.toLowerCase().includes(skill.toLowerCase())
        );
      }

      if (typeof skills === 'string') {
        return skills.toLowerCase().includes(skill.toLowerCase());
      }

      return false;
    });

    return {
      success: true,
      nodeIds: matchingNodes.map((n) => n.id),
      message: `Found ${matchingNodes.length} people with "${skill}" skill`,
      count: matchingNodes.length,
    };
  }

  private findNodeByName(name: string, nodes: NodeData[]): NodeData | undefined {
    const lowerName = name.toLowerCase().trim();

    const exactMatch = nodes.find((node) => {
      const nodeName = node.data?.['name'];
      return typeof nodeName === 'string' && nodeName.toLowerCase() === lowerName;
    });

    if (exactMatch) return exactMatch;

    return nodes.find((node) => {
      const nodeName = node.data?.['name'];
      return typeof nodeName === 'string' && nodeName.toLowerCase().includes(lowerName);
    });
  }

  private isReportingEdge(edge: EdgeData): boolean {
    const type = edge.type.toLowerCase();
    return isOrgPrimaryReportingEdge(edge) || type === 'reports-to';
  }
}
